// Workshop Form page for KATA Dashboard (Create/Edit)

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkshopStore, useCurrentWorkshop } from '../stores/workshopStore';
import { DepartmentService } from '../services/department';
import { UserService, User } from '../services/user';
import { WorkshopCreateData, WorkshopUpdateData } from '../services/workshop';
import SearchableUserDropdown from '../components/common/SearchableUserDropdown';
import { getGroupedOptions, GroupedOptions, WorkshopDropdownOption } from '../services/workshopOptions';

const WorkshopForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const { fetchWorkshop, createWorkshop, updateWorkshop, clearCurrentWorkshop, isCreating, isUpdating, error } = useWorkshopStore();
  const { workshop } = useCurrentWorkshop();
  
  const [departments, setDepartments] = useState<Array<{ id: number; name: string; code: string }>>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [dropdownOptions, setDropdownOptions] = useState<GroupedOptions>({});
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [formData, setFormData] = useState<WorkshopCreateData>({
    title: '',
    status: 'new',
    progress_rate: 0,
    startup_date: '',
    deadline: '',
    mentor: undefined,
    estimated_savings_per_year: 0,
    estimated_savings_unit: 'EUR',
    department: undefined,
    improvement_description: '',
    sqdcm_category: '',
    initial_condition: undefined,
    target_condition: undefined,
    // New fields from Excel analysis
    product_line: '',
    alv_id: '',
    location: '',
    category: '',
    ebit_bridge_lever: '',
    impact_type: '',
    workstream: '',
    aps_maturity: '',
    ws_area: '',
    l_gate: '',
    priority: '',
    description: '',
    // Additional fields
    mentees: [],
    kpi: '',
    indicator_frequency: 'daily',
    current_condition: undefined,
    released_savings: undefined,
  });

  // Load workshop data for editing
  useEffect(() => {
    if (isEdit && id) {
      fetchWorkshop(parseInt(id));
    }
    
    return () => {
      clearCurrentWorkshop();
    };
  }, [isEdit, id, fetchWorkshop, clearCurrentWorkshop]);

  // Calculate progress and released savings dynamically
  const calculateProgress = () => {
    const { initial_condition, target_condition, current_condition } = formData;
    
    if (!target_condition || !initial_condition || !current_condition) {
      return 0;
    }
    
    if (target_condition === initial_condition) {
      return 0;
    }
    
    const progress = ((current_condition - initial_condition) / 
                     (target_condition - initial_condition)) * 100;
    
    return Math.max(0, Math.min(100, progress)); // Clamp between 0-100
  };

  const calculateReleasedSavings = () => {
    const { estimated_savings_per_year } = formData;
    const progress = calculateProgress();
    
    if (!estimated_savings_per_year || progress < 100) {
      return 0;
    }
    
    // Released Savings = Estimated Savings when progress is 100
    // Round to 2 decimal places to match backend constraint
    return Math.round(estimated_savings_per_year * 100) / 100; // Round to 2 decimal places
  };

  // Helper function to format unit display
  const formatUnitDisplay = (unit: string | undefined) => {
    switch (unit) {
      case 'EUR': return '€';
      case 'KEUR': return 'K€';
      case 'MEUR': return 'M€';
      default: return '€';
    }
  };

  // Helper function to render dynamic dropdown options
  const renderOptions = (attributeName: string) => {
    const options = dropdownOptions[attributeName] || [];
    return options.map((option: WorkshopDropdownOption) => (
      <option key={option.id} value={option.value}>
        {option.label}
      </option>
    ));
  };

  // Update calculated fields when input fields change
  useEffect(() => {
    const progress = calculateProgress();
    const releasedSavings = calculateReleasedSavings();
    
    setFormData(prev => ({
      ...prev,
      progress_rate: progress,
      released_savings: releasedSavings
    }));
  }, [formData.initial_condition, formData.target_condition, 
      formData.current_condition, formData.estimated_savings_per_year, formData.estimated_savings_unit]);

  // Auto-generate description based on form inputs
  useEffect(() => {
    const generateDescription = () => {
      const {
        alv_id,
        title,
        ws_area,
        kpi,
        initial_condition,
        target_condition,
        deadline,
        estimated_savings_per_year
      } = formData;

      // Only generate if we have the key fields
      if (alv_id && title && ws_area && kpi && initial_condition && target_condition && deadline && estimated_savings_per_year) {
        const description = `The ${alv_id} project ${title} for ${ws_area} improves ${kpi} From ${initial_condition} To ${target_condition} By ${deadline}, Totaling ${estimated_savings_per_year} financial benefit per year.`;
        
        setFormData(prev => ({
          ...prev,
          description,
          improvement_description: description // Fill both fields with the same content
        }));
      }
    };

    generateDescription();
  }, [
    formData.alv_id,
    formData.title,
    formData.ws_area,
    formData.kpi,
    formData.initial_condition,
    formData.target_condition,
    formData.deadline,
    formData.estimated_savings_per_year
  ]);
  useEffect(() => {
    loadDepartments();
    loadUsers();
    loadDropdownOptions();
  }, []);

  // Load dropdown options from API
  const loadDropdownOptions = async () => {
    try {
      setOptionsLoading(true);
      const options = await getGroupedOptions();
      setDropdownOptions(options);
    } catch (err) {
      console.error('Failed to load dropdown options:', err);
      // Continue with empty options - form will still work
    } finally {
      setOptionsLoading(false);
    }
  };

  // Helper function to find user ID by name
  const findUserIdByName = (name: string): number | undefined => {
    if (!name || !users.length) return undefined;
    
    // Try to find user by full name match
    const user = users.find(u => {
      const fullName = `${u.first_name} ${u.last_name}`.trim();
      return fullName === name || u.username === name;
    });
    
    return user?.id;
  };

  // Helper function to parse mentor (handle both ID and name)
  const parseMentorValue = (mentorValue: string | number | null): number | undefined => {
    if (!mentorValue) return undefined;
    
    if (typeof mentorValue === 'number') {
      return mentorValue;
    }
    
    // If it's a string, check if it's a numeric ID or a name
    const mentorStr = mentorValue.toString();
    if (/^\d+$/.test(mentorStr)) {
      // It's a numeric string (ID)
      return parseInt(mentorStr);
    } else {
      // It's a name, try to find the user ID
      return findUserIdByName(mentorStr);
    }
  };

  // Helper function to parse mentees (handle both IDs and names)
  const parseMenteesValue = (menteesValue: string[] | number[] | null): number[] => {
    if (!Array.isArray(menteesValue)) return [];
    
    return menteesValue.map(mentee => {
      if (typeof mentee === 'number') {
        return mentee;
      }
      
      // If it's a string, check if it's a numeric ID or a name
      const menteeStr = mentee.toString();
      if (/^\d+$/.test(menteeStr)) {
        // It's a numeric string (ID)
        return parseInt(menteeStr);
      } else {
        // It's a name, try to find the user ID
        const userId = findUserIdByName(menteeStr);
        return userId || 0; // Return 0 if not found (will be filtered out)
      }
    }).filter(id => id > 0); // Remove invalid IDs
  };

  // Populate form when workshop data is loaded
  useEffect(() => {
    if (isEdit && workshop && users.length > 0) { // Wait for users to be loaded
      setFormData({
        title: workshop.title,
        status: workshop.status,
        progress_rate: workshop.progress_rate || 0,
        startup_date: workshop.startup_date || '',
        deadline: workshop.deadline || '',
        mentor: parseMentorValue(workshop.mentor),
        estimated_savings_per_year: parseFloat(workshop.estimated_savings_per_year || '0'),
        estimated_savings_unit: workshop.estimated_savings_unit || 'EUR',
        department: workshop.department || undefined,
        improvement_description: workshop.improvement_description || '',
        sqdcm_category: workshop.sqdcm_category || '',
        initial_condition: workshop.initial_condition || undefined,
        target_condition: workshop.target_condition || undefined,
        // New fields from Excel analysis
        product_line: workshop.product_line || '',
        alv_id: workshop.alv_id || '',
        location: workshop.location || '',
        category: workshop.category || '',
        ebit_bridge_lever: workshop.ebit_bridge_lever || '',
        impact_type: workshop.impact_type || '',
        workstream: workshop.workstream || '',
        aps_maturity: workshop.aps_maturity || '',
        ws_area: workshop.ws_area || '',
        l_gate: workshop.l_gate || '',
        priority: workshop.priority || '',
        description: workshop.description || '',
        // Additional fields
        mentees: parseMenteesValue(workshop.mentees),
        kpi: workshop.kpi || '',
        indicator_frequency: (workshop.indicator_frequency as 'daily' | 'weekly' | 'monthly') || 'daily',
        current_condition: workshop.current_condition || undefined,
        released_savings: parseFloat(workshop.released_savings || '0') || undefined,
      });
    }
  }, [isEdit, workshop, users]); // Add users as dependency

  const loadDepartments = async () => {
    try {
      const deptOptions = await DepartmentService.getDepartmentOptions();
      setDepartments(deptOptions);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const fetchedUsers = await UserService.getAllUsers({ is_active: true });
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // Validation functions
  const validateFormWithData = (data: typeof formData): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Title validation
    if (!data.title?.trim()) {
      errors.title = 'Workshop title is required';
    }

    // Department validation
    if (!data.department) {
      errors.department = 'Department is required';
    }

    // Date validation
    if (data.startup_date && data.deadline) {
      const startupDate = new Date(data.startup_date);
      const deadlineDate = new Date(data.deadline);
      
      // console.log('Date validation:', {
      //   startup_date: data.startup_date,
      //   deadline: data.deadline,
      //   startupDate: startupDate.toISOString(),
      //   deadlineDate: deadlineDate.toISOString(),
      //   isStartupAfterDeadline: startupDate > deadlineDate
      // });
      
      if (startupDate > deadlineDate) {
        errors.dates = 'Startup date cannot be after deadline';
      }
    }

    // Progress rate validation
    if (data.progress_rate !== undefined && data.progress_rate !== null) {
      if (data.progress_rate < 0 || data.progress_rate > 100) {
        errors.progress_rate = 'Progress rate must be between 0 and 100';
      }
    }

    // Estimated savings validation
    if (data.estimated_savings_per_year !== undefined && data.estimated_savings_per_year !== null) {
      if (data.estimated_savings_per_year < 0) {
        errors.estimated_savings_per_year = 'Estimated savings cannot be negative';
      }
    }

    // Note: Condition fields (initial, current, target) validation removed
    // Users can set any values - conditions can increase or decrease
    // Users are responsible for their data and can edit workshops later if needed

    return errors;
  };

  const validateForm = (): Record<string, string> => {
    return validateFormWithData(formData);
  };

  const isFormValid = (): boolean => {
    const errors = validateForm();
    
    // Note: Condition fields validation removed - users can set any values
    // Conditions can increase or decrease, users take responsibility
    
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    if (type === 'number') {
      processedValue = value ? parseFloat(value) : undefined;
    } else if (name === 'mentor') {
      processedValue = value ? parseInt(value) : undefined;
    }
    
    const newFormData = {
      ...formData,
      [name]: processedValue
    };
    
    setFormData(newFormData);

    // Clear validation errors when user starts typing
    if (validationErrors[name] || validationErrors.dates) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        // Clear date error if either date field is changed
        if (name === 'startup_date' || name === 'deadline') {
          delete newErrors.dates;
        }
        return newErrors;
      });
    }

    // Trigger real-time validation for date fields
    if (name === 'startup_date' || name === 'deadline') {
      setTimeout(() => {
        const errors = validateFormWithData(newFormData);
        setValidationErrors(errors);
      }, 100); // Small delay to ensure state is updated
    }
  };

  const handleMentorChange = (selectedUserIds: number[]) => {
    setFormData({
      ...formData,
      mentor: selectedUserIds.length > 0 ? selectedUserIds[0] : undefined
    });
  };

  const handleMenteesChange = (selectedUserIds: number[]) => {
    setFormData({
      ...formData,
      mentees: selectedUserIds
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submitting
    const errors = validateForm();
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField === 'dates' ? 'startup_date' : firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    try {
      if (isEdit && workshop) {
        const updateData: WorkshopUpdateData = { ...formData };
        await updateWorkshop(workshop.id, updateData);
        navigate(`/workshops/${workshop.id}`);
      } else {
        const newWorkshop = await createWorkshop(formData);
        navigate(`/workshops/${newWorkshop.id}`);
      }
    } catch (error) {
      // Error is handled by the store
      console.error('Form submission error:', error);
    }
  };

  const isSubmitting = isCreating || isUpdating;

  // Error message component
  const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
    <p className="mt-1 text-sm text-red-600 flex items-center">
      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/workshops')}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEdit ? 'Edit Workshop' : 'New Workshop'}
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  {isEdit ? 'Update workshop information' : 'Create a new improvement workshop'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Basic Information</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Title */}
                <div className="sm:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Workshop Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      validationErrors.title
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                    placeholder="Enter workshop title"
                  />
                  {validationErrors.title && <ErrorMessage message={validationErrors.title} />}
                </div>

                {/* Status */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Status *
                  </label>
                  <select
                    name="status"
                    id="status"
                    required
                    value={formData.status}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    disabled={optionsLoading}
                  >
                    {renderOptions('status')}
                  </select>
                </div>

                {/* Progress - Read Only */}
                <div>
                  <label htmlFor="progress_rate" className="block text-sm font-medium text-gray-700">
                    Progress Rate (%) - Calculated
                  </label>
                  <div className="mt-1">
                    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 text-sm">
                      {(formData.progress_rate ?? 0).toFixed(2)}%
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(100, Math.max(0, formData.progress_rate ?? 0))}%` }}
                      ></div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Calculated from: (Current - Initial) / (Target - Initial) × 100
                    </p>
                  </div>
                </div>

                {/* Department */}
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="department"
                    id="department"
                    value={formData.department || ''}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name} ({dept.code})
                      </option>
                    ))}
                  </select>
                  {validationErrors.department && <ErrorMessage message={validationErrors.department} />}
                </div>

                {/* Mentor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mentor
                  </label>
                  <SearchableUserDropdown
                    users={users}
                    selectedUsers={formData.mentor ? [formData.mentor] : []}
                    onSelectionChange={handleMentorChange}
                    placeholder="Search and select mentor..."
                    multiple={false}
                    className="mt-1"
                  />
                </div>

                {/* Mentees */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mentees
                  </label>
                  <SearchableUserDropdown
                    users={users}
                    selectedUsers={formData.mentees || []}
                    onSelectionChange={handleMenteesChange}
                    placeholder="Search and select mentees..."
                    multiple={true}
                    className="mt-1"
                  />
                </div>

                {/* Startup Date */}
                <div>
                  <label htmlFor="startup_date" className="block text-sm font-medium text-gray-700">
                    Startup Date
                  </label>
                  <input
                    type="date"
                    name="startup_date"
                    id="startup_date"
                    value={formData.startup_date}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      validationErrors.dates
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                  />
                  {validationErrors.dates && <ErrorMessage message={validationErrors.dates} />}
                </div>

                {/* Deadline */}
                <div>
                  <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
                    Deadline
                  </label>
                  <input
                    type="date"
                    name="deadline"
                    id="deadline"
                    value={formData.deadline}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      validationErrors.dates
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                  />
                  {validationErrors.dates && <ErrorMessage message={validationErrors.dates} />}
                </div>

                {/* Estimated Savings */}
                <div>
                  <label htmlFor="estimated_savings_per_year" className="block text-sm font-medium text-gray-700">
                    Estimated Savings/Year
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="number"
                      name="estimated_savings_per_year"
                      id="estimated_savings_per_year"
                      min="0"
                      value={formData.estimated_savings_per_year}
                      onChange={handleInputChange}
                      className={`flex-1 rounded-l-md border-r-0 sm:text-sm ${
                        validationErrors.estimated_savings_per_year
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                      placeholder="0"
                    />
                    <select
                      name="estimated_savings_unit"
                      id="estimated_savings_unit"
                      value={formData.estimated_savings_unit}
                      onChange={handleInputChange}
                      disabled={optionsLoading}
                      className={`rounded-r-md border-l-0 sm:text-sm ${
                        validationErrors.estimated_savings_unit
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                    >
                      {renderOptions('currency_unit')}
                    </select>
                  </div>
                  {validationErrors.estimated_savings_per_year && <ErrorMessage message={validationErrors.estimated_savings_per_year} />}
                  {validationErrors.estimated_savings_unit && <ErrorMessage message={validationErrors.estimated_savings_unit} />}
                </div>

                {/* Improvement Description */}
                <div className="sm:col-span-2">
                  <label htmlFor="improvement_description" className="block text-sm font-medium text-gray-700">
                    Improvement Description
                  </label>
                  <textarea
                    name="improvement_description"
                    id="improvement_description"
                    rows={4}
                    value={formData.improvement_description}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Describe the improvement objectives and approach"
                  />
                </div>

                {/* Auto-generated Description Preview */}
                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Generated Description Preview
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={3}
                    value={formData.description}
                    readOnly
                    className="mt-1 block w-full border-gray-200 rounded-md shadow-sm bg-gray-50 text-gray-700 sm:text-sm"
                    placeholder="Description will be auto-generated based on form inputs..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This description is automatically generated from: ALV ID, Title, WS Area, KPI, Initial/Target Conditions, Deadline, and Estimated Savings
                  </p>
                </div>
              </div>
            </div>
          </div>


          {/* Categories & KPIs */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Categories & KPIs</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* SQDCM Category */}
                <div>
                  <label htmlFor="sqdcm_category" className="block text-sm font-medium text-gray-700">
                    SQDCM Category
                  </label>
                  <select
                    name="sqdcm_category"
                    id="sqdcm_category"
                    value={formData.sqdcm_category}
                    onChange={handleInputChange}
                    disabled={optionsLoading}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Category</option>
                    {renderOptions('sqdcm_category')}
                  </select>
                </div>

                {/* KPI */}
                <div>
                  <label htmlFor="kpi" className="block text-sm font-medium text-gray-700">
                    KPI
                  </label>
                  <input
                    type="text"
                    name="kpi"
                    id="kpi"
                    value={formData.kpi || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter KPI"
                  />
                </div>

                {/* Indicator Frequency */}
                <div>
                  <label htmlFor="indicator_frequency" className="block text-sm font-medium text-gray-700">
                    Indicator Frequency
                  </label>
                  <select
                    name="indicator_frequency"
                    id="indicator_frequency"
                    value={formData.indicator_frequency || 'daily'}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Frequency of indicator data input: Daily (dates), Weekly (week numbers), or Monthly (month names)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Classification */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Product & Location</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Product Line */}
                <div>
                  <label htmlFor="product_line" className="block text-sm font-medium text-gray-700">
                    Product Line
                  </label>
                  <select
                    name="product_line"
                    id="product_line"
                    value={formData.product_line}
                    onChange={handleInputChange}
                    disabled={optionsLoading}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Product Line</option>
                    {renderOptions('product_line')}
                  </select>
                </div>

                {/* ALV ID */}
                <div>
                  <label htmlFor="alv_id" className="block text-sm font-medium text-gray-700">
                    ALV ID
                  </label>
                  <select
                    name="alv_id"
                    id="alv_id"
                    value={formData.alv_id}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('alv_id')}
                  </select>
                </div>

                {/* WS Area */}
                <div className="sm:col-span-2">
                  <label htmlFor="ws_area" className="block text-sm font-medium text-gray-700">
                    WS Area
                  </label>
                  <select
                    name="ws_area"
                    id="ws_area"
                    value={formData.ws_area}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('ws_area')}
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <select
                    name="location"
                    id="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('location')}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('category')}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Business Classification */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Business Classification</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* EBIT Bridge Lever */}
                <div>
                  <label htmlFor="ebit_bridge_lever" className="block text-sm font-medium text-gray-700">
                    EBIT Bridge Lever
                  </label>
                  <select
                    name="ebit_bridge_lever"
                    id="ebit_bridge_lever"
                    value={formData.ebit_bridge_lever}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('ebit_bridge_lever')}
                  </select>
                </div>

                {/* Impact Type */}
                <div>
                  <label htmlFor="impact_type" className="block text-sm font-medium text-gray-700">
                    Impact Type
                  </label>
                  <select
                    name="impact_type"
                    id="impact_type"
                    value={formData.impact_type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('impact_type')}
                  </select>
                </div>

                {/* Workstream */}
                <div>
                  <label htmlFor="workstream" className="block text-sm font-medium text-gray-700">
                    Workstream
                  </label>
                  <select
                    name="workstream"
                    id="workstream"
                    value={formData.workstream}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('workstream')}
                  </select>
                </div>

                {/* APS Maturity */}
                <div>
                  <label htmlFor="aps_maturity" className="block text-sm font-medium text-gray-700">
                    APS Maturity
                  </label>
                  <select
                    name="aps_maturity"
                    id="aps_maturity"
                    value={formData.aps_maturity}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('aps_maturity')}
                  </select>
                </div>

                {/* L-Gate */}
                <div>
                  <label htmlFor="l_gate" className="block text-sm font-medium text-gray-700">
                    L-Gate
                  </label>
                  <select
                    name="l_gate"
                    id="l_gate"
                    value={formData.l_gate}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('l_gate')}
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                    Priority
                  </label>
                  <select
                    name="priority"
                    id="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  
                    disabled={optionsLoading}
                  >
                    {renderOptions('priority')}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Conditions & Savings */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Conditions & Savings</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Initial Condition */}
                <div>
                  <label htmlFor="initial_condition" className="block text-sm font-medium text-gray-700">
                    Initial Condition
                  </label>
                  <input
                    type="number"
                    name="initial_condition"
                    id="initial_condition"
                    step="0.1"
                    value={formData.initial_condition || ''}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      validationErrors.initial_condition
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                    placeholder="Enter initial value"
                  />
                  {validationErrors.initial_condition && <ErrorMessage message={validationErrors.initial_condition} />}
                </div>

                {/* Current Condition */}
                <div>
                  <label htmlFor="current_condition" className="block text-sm font-medium text-gray-700">
                    Current Condition
                  </label>
                  <input
                    type="number"
                    name="current_condition"
                    id="current_condition"
                    step="0.1"
                    value={formData.current_condition || ''}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      validationErrors.current_condition
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                    placeholder="Enter current value"
                  />
                  {validationErrors.current_condition && <ErrorMessage message={validationErrors.current_condition} />}
                </div>

                {/* Target Condition */}
                <div>
                  <label htmlFor="target_condition" className="block text-sm font-medium text-gray-700">
                    Target Condition
                  </label>
                  <input
                    type="number"
                    name="target_condition"
                    id="target_condition"
                    step="0.1"
                    value={formData.target_condition || ''}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      validationErrors.target_condition
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                    placeholder="Enter target value"
                  />
                  {validationErrors.target_condition && <ErrorMessage message={validationErrors.target_condition} />}
                </div>

                {/* Released Savings - Read Only */}
                <div>
                  <label htmlFor="released_savings" className="block text-sm font-medium text-gray-700">
                    Released Savings ({formatUnitDisplay(formData.estimated_savings_unit)}) - Calculated
                  </label>
                  <div className="mt-1">
                    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 text-sm">
                      {formatUnitDisplay(formData.estimated_savings_unit)}{(formData.released_savings ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Shows Estimated Savings when Progress is 100%, otherwise 0
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/workshops')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid()}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEdit ? 'Update Workshop' : 'Create Workshop'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkshopForm;
